import { clsx, type ClassValue } from 'clsx'

/**
 * Merge class names with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

/**
 * Convert kg to lbs
 */
export function kgToLbs(kg: number): number {
  return kg * 2.20462
}

/**
 * Convert lbs to kg
 */
export function lbsToKg(lbs: number): number {
  return lbs / 2.20462
}

/**
 * Format weight based on unit system
 */
export function formatWeight(
  kg: number,
  unitSystem: 'imperial' | 'metric',
  decimals: number = 1
): string {
  if (unitSystem === 'imperial') {
    return `${kgToLbs(kg).toFixed(decimals)} lbs`
  }
  return `${kg.toFixed(decimals)} kg`
}

/**
 * Parse weight input to kg (handles both units)
 */
export function parseWeightToKg(
  value: number,
  unitSystem: 'imperial' | 'metric'
): number {
  if (unitSystem === 'imperial') {
    return lbsToKg(value)
  }
  return value
}

/**
 * Format duration in minutes to human readable
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`
}

/**
 * Format date for display
 */
export function formatDate(date: Date | string, format: 'short' | 'long' | 'relative' = 'short'): string {
  const d = typeof date === 'string' ? new Date(date) : date

  if (format === 'relative') {
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return `${days} days ago`
  }

  if (format === 'long') {
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  // Short format
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Calculate volume (sets × reps × weight)
 */
export function calculateVolume(
  sets: Array<{ weight_kg: number; reps: number; set_type: string }>
): number {
  return sets
    .filter((s) => s.set_type === 'working')
    .reduce((total, set) => total + set.weight_kg * set.reps, 0)
}

/**
 * Estimate 1RM using Epley formula
 */
export function estimate1RM(weight: number, reps: number): number {
  if (reps === 1) return weight
  return weight * (1 + reps / 30)
}

/**
 * Calculate RIR color class
 */
export function getRIRColor(rir: number): string {
  if (rir === 0) return 'text-error'
  if (rir <= 2) return 'text-warning'
  return 'text-success'
}

/**
 * Get workout type display name
 */
export function getWorkoutDisplayName(workoutType: string): string {
  const names: Record<string, string> = {
    chest_push: 'Chest Push',
    pull_a: 'Pull A',
    legs: 'Legs (Quad)',
    shoulder_push: 'Shoulder Push',
    pull_b: 'Pull B',
    ham_legs: 'Legs (Ham)',
  }
  return names[workoutType] || workoutType
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Sleep/delay utility
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Check if we're online
 */
export function isOnline(): boolean {
  return navigator.onLine
}

/**
 * Generate a simple hash for comparison
 */
export function simpleHash(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return hash
}
